import React from 'react';
import {
  ClockIcon,
  EyeIcon,
  LinkIcon,
  RectangleStackIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

import Layout from '../../components/layout/Layout';
import {
  Alert,
  Badge,
  MetricCard,
  Page,
  PageBody,
  PageHeader,
  Panel,
  PanelBody,
  PanelDescription,
  PanelHeader,
  PanelTitle,
  Spinner,
} from '../../components/ui';
import {
  useGetAccountRollupsV1OpportunityDiagnosticIntelligenceAccountsGet,
  useGetArchetypesV1OpportunityDiagnosticIntelligenceArchetypesGet,
  useGetSummaryV1OpportunityDiagnosticIntelligenceSummaryGet,
} from '../../generated/opportunity-diagnostic-intelligence/opportunity-diagnostic-intelligence';

interface CountBucket {
  key: string;
  count: number;
}

interface UsageSummary {
  access_link_count: number;
  active_access_link_count: number;
  redeemed_access_link_count: number;
  expired_access_link_count: number;
  revoked_access_link_count: number;
  viewer_session_count: number;
  active_viewer_session_count: number;
  unique_viewer_count: number;
  unique_viewer_domain_count: number;
  public_open_count: number;
  public_save_count: number;
  forge_save_count: number;
  share_count: number;
  import_count: number;
  export_count: number;
  last_activity_at?: string | null;
}

interface ViewerDomainUsage {
  viewer_email_domain: string;
  unique_viewer_count: number;
  viewer_session_count: number;
  opened_count: number;
  saved_count: number;
  workspace_count: number;
  last_seen_at?: string | null;
}

interface WorkspaceEngagement {
  workspace_id: number;
  workspace_title: string;
  account_id: number;
  account_name: string;
  parent_account_id?: number | null;
  parent_account_name?: string | null;
  industry?: string | null;
  current_revision_number: number;
  opportunity_count: number;
  access_link_count: number;
  active_access_link_count: number;
  viewer_session_count: number;
  unique_viewer_count: number;
  opened_count: number;
  saved_count: number;
  shared_count: number;
  import_count: number;
  export_count: number;
  event_count: number;
  viewer_domains: string[];
  latest_revision_at?: string | null;
  last_activity_at?: string | null;
}

interface DashboardSummary {
  workspace_count: number;
  revision_count: number;
  opportunity_count: number;
  account_count?: number;
  client_account_count?: number;
  holding_company_count?: number;
  usage?: UsageSummary;
  source_counts?: CountBucket[];
  status_counts?: CountBucket[];
  theme_counts?: CountBucket[];
  score_buckets?: Array<{ bucket: string; count: number }>;
  event_counts?: CountBucket[];
  viewer_domain_usage?: ViewerDomainUsage[];
  workspace_engagement?: WorkspaceEngagement[];
}

function topLabel(rows: Array<{ key: string; count: number }> | undefined): string {
  if (!rows?.length) return '—';
  return [...rows].sort((a, b) => b.count - a.count)[0].key;
}

export default function OpportunityDiagnosticIntelligencePage() {
  const summaryQuery = useGetSummaryV1OpportunityDiagnosticIntelligenceSummaryGet({
    query: { queryKey: ['opportunity-diagnostic-intelligence', 'summary'] },
  });
  const accountsQuery = useGetAccountRollupsV1OpportunityDiagnosticIntelligenceAccountsGet({
    query: { queryKey: ['opportunity-diagnostic-intelligence', 'accounts'] },
  });
  const archetypesQuery = useGetArchetypesV1OpportunityDiagnosticIntelligenceArchetypesGet({
    query: { queryKey: ['opportunity-diagnostic-intelligence', 'archetypes'] },
  });

  const summary = summaryQuery.data as DashboardSummary | undefined;
  const usage = summary?.usage ?? emptyUsage();
  const isLoading = summaryQuery.isLoading || accountsQuery.isLoading || archetypesQuery.isLoading;
  const isError = summaryQuery.isError || accountsQuery.isError || archetypesQuery.isError;

  return (
    <Layout>
      <Page layout="full-width" maxWidth="full">
        <PageHeader
          title="Opportunity Diagnostic Intelligence"
          description="Internal Eliza dashboard for ranker adoption, client workspace activity, access usage, and aggregate opportunity patterns."
        />
        <PageBody className="space-y-5">
          <Alert variant="info">
            Usage analytics are privacy-safe: domains, counts, hashes, and timestamps only. Raw viewer emails, notes,
            and opportunity details stay out of the default dashboard.
          </Alert>

          {isError && (
            <Alert variant="error">Unable to load diagnostic intelligence rollups.</Alert>
          )}

          {isLoading ? (
            <div className="flex min-h-64 items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                <MetricCard
                  title="Client Workspaces"
                  value={summary?.workspace_count ?? 0}
                  icon={<RectangleStackIcon className="h-5 w-5" />}
                />
                <MetricCard
                  title="Public Sessions"
                  value={usage.viewer_session_count}
                  changeLabel={`${usage.active_viewer_session_count} active`}
                  icon={<UserGroupIcon className="h-5 w-5" />}
                />
                <MetricCard
                  title="Access Links"
                  value={usage.access_link_count}
                  changeLabel={`${usage.redeemed_access_link_count} redeemed`}
                  icon={<LinkIcon className="h-5 w-5" />}
                />
                <MetricCard
                  title="Public Opens"
                  value={usage.public_open_count}
                  changeLabel={`${usage.public_save_count} client saves`}
                  icon={<EyeIcon className="h-5 w-5" />}
                />
                <MetricCard
                  title="Last Activity"
                  value={formatShortDate(usage.last_activity_at)}
                  icon={<ClockIcon className="h-5 w-5" />}
                />
              </div>

              <div className="grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)]">
                <Panel>
                  <PanelHeader>
                    <PanelTitle>Workspace Engagement</PanelTitle>
                    <PanelDescription>
                      Public link activity, saves, imports, exports, and viewer domains by client workspace.
                    </PanelDescription>
                  </PanelHeader>
                  <PanelBody className="overflow-x-auto p-0">
                    <table className="min-w-[1180px] divide-y divide-gray-200 text-sm dark:divide-dark-border">
                      <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-dark-surface-2 dark:text-gray-400">
                        <tr>
                          <th className="px-4 py-3">Client / workspace</th>
                          <th className="px-4 py-3">Holding Co.</th>
                          <th className="px-4 py-3">Content</th>
                          <th className="px-4 py-3">Access</th>
                          <th className="px-4 py-3">Usage</th>
                          <th className="px-4 py-3">Domains</th>
                          <th className="px-4 py-3">Last activity</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                        {(summary?.workspace_engagement ?? []).map((row) => (
                          <tr key={row.workspace_id} className="bg-white dark:bg-dark-surface">
                            <td className="px-4 py-3 align-top">
                              <div className="font-medium text-charcoal dark:text-white">{row.account_name}</div>
                              <div className="text-gray-500 dark:text-gray-400">{row.workspace_title}</div>
                              {row.industry && <div className="mt-1 text-xs text-gray-400">{row.industry}</div>}
                            </td>
                            <td className="px-4 py-3 align-top">{row.parent_account_name ?? '—'}</td>
                            <td className="px-4 py-3 align-top">
                              <MetricStack
                                items={[
                                  ['opps', row.opportunity_count],
                                  ['rev', row.current_revision_number],
                                  ['events', row.event_count],
                                ]}
                              />
                            </td>
                            <td className="px-4 py-3 align-top">
                              <MetricStack
                                items={[
                                  ['links', `${row.active_access_link_count}/${row.access_link_count}`],
                                  ['viewers', row.unique_viewer_count],
                                  ['sessions', row.viewer_session_count],
                                ]}
                              />
                            </td>
                            <td className="px-4 py-3 align-top">
                              <MetricStack
                                items={[
                                  ['opens', row.opened_count],
                                  ['saves', row.saved_count],
                                  ['imports / exports', `${row.import_count}/${row.export_count}`],
                                ]}
                              />
                            </td>
                            <td className="max-w-[220px] px-4 py-3 align-top">
                              <DomainBadges domains={row.viewer_domains} />
                            </td>
                            <td className="px-4 py-3 align-top text-gray-600 dark:text-gray-300">
                              {formatDateTime(row.last_activity_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader>
                    <PanelTitle>Access Usage</PanelTitle>
                    <PanelDescription>
                      Funnel and activity mix for public client access.
                    </PanelDescription>
                  </PanelHeader>
                  <PanelBody className="space-y-5">
                    <FunnelRow label="Links created" value={usage.access_link_count} max={usage.access_link_count} />
                    <FunnelRow label="Links redeemed" value={usage.redeemed_access_link_count} max={usage.access_link_count} />
                    <FunnelRow label="Viewer sessions" value={usage.viewer_session_count} max={usage.access_link_count} />
                    <FunnelRow label="Client saves" value={usage.public_save_count} max={Math.max(usage.viewer_session_count, 1)} />
                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <MiniStat label="Active links" value={usage.active_access_link_count} />
                      <MiniStat label="Expired links" value={usage.expired_access_link_count} />
                      <MiniStat label="Revoked links" value={usage.revoked_access_link_count} />
                      <MiniStat label="Viewer domains" value={usage.unique_viewer_domain_count} />
                    </div>
                    <div>
                      <h3 className="mb-2 text-sm font-semibold text-charcoal dark:text-gray-100">Event mix</h3>
                      <BucketList rows={summary?.event_counts ?? []} />
                    </div>
                  </PanelBody>
                </Panel>
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <Panel>
                  <PanelHeader>
                    <PanelTitle>Viewer Domain Usage</PanelTitle>
                    <PanelDescription>
                      “Who used access” shown as domains and anonymized counts, not raw email addresses.
                    </PanelDescription>
                  </PanelHeader>
                  <PanelBody className="overflow-x-auto p-0">
                    <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-dark-border">
                      <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-dark-surface-2 dark:text-gray-400">
                        <tr>
                          <th className="px-4 py-3">Domain</th>
                          <th className="px-4 py-3">Viewers</th>
                          <th className="px-4 py-3">Sessions</th>
                          <th className="px-4 py-3">Opens</th>
                          <th className="px-4 py-3">Saves</th>
                          <th className="px-4 py-3">Last seen</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                        {(summary?.viewer_domain_usage ?? []).map((row) => (
                          <tr key={row.viewer_email_domain} className="bg-white dark:bg-dark-surface">
                            <td className="px-4 py-3 font-medium text-charcoal dark:text-white">
                              {row.viewer_email_domain}
                            </td>
                            <td className="px-4 py-3">{row.unique_viewer_count}</td>
                            <td className="px-4 py-3">{row.viewer_session_count}</td>
                            <td className="px-4 py-3">{row.opened_count}</td>
                            <td className="px-4 py-3">{row.saved_count}</td>
                            <td className="px-4 py-3">{formatDateTime(row.last_seen_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader>
                    <PanelTitle>Portfolio Snapshot</PanelTitle>
                    <PanelDescription>
                      What Eliza can say across clients and holding companies.
                    </PanelDescription>
                  </PanelHeader>
                  <PanelBody className="grid gap-3 sm:grid-cols-2">
                    <MiniStat label="Accounts" value={summary?.account_count ?? 0} />
                    <MiniStat label="Client accounts" value={summary?.client_account_count ?? 0} />
                    <MiniStat label="Holding companies" value={summary?.holding_company_count ?? 0} />
                    <MiniStat label="Opportunities" value={summary?.opportunity_count ?? 0} />
                    <MiniStat label="Top source" value={topLabel(summary?.source_counts)} />
                    <MiniStat label="Top status" value={topLabel(summary?.status_counts)} />
                  </PanelBody>
                </Panel>
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <Panel>
                  <PanelHeader>
                    <PanelTitle>Theme Rollups</PanelTitle>
                    <PanelDescription>Aggregate counts only; no raw opportunity details.</PanelDescription>
                  </PanelHeader>
                  <PanelBody className="p-0">
                    <BucketTable rows={summary?.theme_counts ?? []} firstColumn="Theme" />
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader>
                    <PanelTitle>Opportunity Archetypes</PanelTitle>
                    <PanelDescription>Strategic tags across saved workspace revisions.</PanelDescription>
                  </PanelHeader>
                  <PanelBody className="overflow-x-auto p-0">
                    <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-dark-border">
                      <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-dark-surface-2 dark:text-gray-400">
                        <tr>
                          <th className="px-4 py-3">Archetype</th>
                          <th className="px-4 py-3">Count</th>
                          <th className="px-4 py-3">Avg. Composite</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                        {(archetypesQuery.data?.items ?? []).map((row) => (
                          <tr key={row.archetype} className="bg-white dark:bg-dark-surface">
                            <td className="px-4 py-3 font-medium text-charcoal dark:text-white">{row.archetype}</td>
                            <td className="px-4 py-3">{row.count}</td>
                            <td className="px-4 py-3">{row.average_composite_score ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </PanelBody>
                </Panel>
              </div>

              <Panel>
                <PanelHeader>
                  <PanelTitle>Account Rollups</PanelTitle>
                  <PanelDescription>Client and holding-company relationships with aggregate scoring.</PanelDescription>
                </PanelHeader>
                <PanelBody className="overflow-x-auto p-0">
                  <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-dark-border">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-dark-surface-2 dark:text-gray-400">
                      <tr>
                        <th className="px-4 py-3">Account</th>
                        <th className="px-4 py-3">Holding Co.</th>
                        <th className="px-4 py-3">Industry</th>
                        <th className="px-4 py-3">Workspaces</th>
                        <th className="px-4 py-3">Opportunities</th>
                        <th className="px-4 py-3">Avg. Composite</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                      {(accountsQuery.data?.items ?? []).map((row) => (
                        <tr key={row.account_id} className="bg-white dark:bg-dark-surface">
                          <td className="px-4 py-3 font-medium text-charcoal dark:text-white">{row.account_name}</td>
                          <td className="px-4 py-3">{(row as { parent_account_name?: string | null }).parent_account_name ?? '—'}</td>
                          <td className="px-4 py-3">{row.industry ?? '—'}</td>
                          <td className="px-4 py-3">{row.workspace_count}</td>
                          <td className="px-4 py-3">{row.opportunity_count}</td>
                          <td className="px-4 py-3">{row.average_composite_score ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </PanelBody>
              </Panel>
            </>
          )}
        </PageBody>
      </Page>
    </Layout>
  );
}

function emptyUsage(): UsageSummary {
  return {
    access_link_count: 0,
    active_access_link_count: 0,
    redeemed_access_link_count: 0,
    expired_access_link_count: 0,
    revoked_access_link_count: 0,
    viewer_session_count: 0,
    active_viewer_session_count: 0,
    unique_viewer_count: 0,
    unique_viewer_domain_count: 0,
    public_open_count: 0,
    public_save_count: 0,
    forge_save_count: 0,
    share_count: 0,
    import_count: 0,
    export_count: 0,
    last_activity_at: null,
  };
}

function MetricStack({ items }: { items: Array<[string, string | number]> }) {
  return (
    <dl className="space-y-1">
      {items.map(([label, value]) => (
        <div key={label} className="flex min-w-32 justify-between gap-3">
          <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
          <dd className="font-medium text-charcoal dark:text-gray-100">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function MiniStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 text-xl font-semibold text-charcoal dark:text-gray-100">{value}</div>
    </div>
  );
}

function FunnelRow({ label, value, max }: { label: string; value: number; max: number }) {
  const width = max <= 0 ? 0 : Math.min(100, Math.round((value / max) * 100));
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="font-medium text-charcoal dark:text-gray-100">{label}</span>
        <span className="text-gray-500 dark:text-gray-400">{value}</span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 dark:bg-dark-surface-2">
        <div className="h-full rounded-full bg-eliza-red" style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function DomainBadges({ domains }: { domains: string[] }) {
  if (!domains.length) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {domains.slice(0, 3).map((domain) => (
        <Badge key={domain} variant="secondary">{domain}</Badge>
      ))}
      {domains.length > 3 && <Badge variant="secondary">+{domains.length - 3}</Badge>}
    </div>
  );
}

function BucketList({ rows }: { rows: CountBucket[] }) {
  if (!rows.length) return <p className="text-sm text-gray-500 dark:text-gray-400">No events recorded yet.</p>;
  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <div key={row.key} className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2 dark:bg-dark-surface-2">
          <span className="text-sm text-charcoal dark:text-gray-100">{eventLabel(row.key)}</span>
          <Badge variant="secondary">{row.count}</Badge>
        </div>
      ))}
    </div>
  );
}

function BucketTable({ rows, firstColumn }: { rows: CountBucket[]; firstColumn: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-dark-border">
        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-dark-surface-2 dark:text-gray-400">
          <tr>
            <th className="px-4 py-3">{firstColumn}</th>
            <th className="px-4 py-3">Count</th>
            <th className="px-4 py-3">Boundary</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
          {rows.map((row) => (
            <tr key={row.key} className="bg-white dark:bg-dark-surface">
              <td className="px-4 py-3 font-medium text-charcoal dark:text-white">{row.key}</td>
              <td className="px-4 py-3">{row.count}</td>
              <td className="px-4 py-3"><Badge variant="secondary">aggregate only</Badge></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function eventLabel(value: string) {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatShortDate(value?: string | null) {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(value));
}

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}
